/**
 * Modal controller.
 *
 * This class controls site modal overlays.
 */

 // Use jQuery as $ within this file scope.
 const $ = jQuery;

/**
 * Class Modal
 */
export default class Modal {

    /**
     * Class constructor is for binding class properties.
     */
    constructor() {

        // Stores the currently opened modal.
        this.$currentOpenModal = null;

        // Stores the element that opened the modal.
        this.focusedElBeforeOpen = null;

        // Stores the first focusable element in the opened the modal.
        this.$currentFirstFocusable = null;

        // Stores the last focusable element in the opened the modal.
        this.$currentLastFocusable = null;
    }

    /**
     * Cache dom elements for use in the class's methods
     */
    cache() {
        this.$body = $( 'body' );
    }

    /**
     * All common events
     * @return {void}
     */
    events() {
        document.addEventListener( 'keydown', evt => this.keyDownHandler( evt ) );

        // Check if user has clicked outside an open checkbox dropdown
        document.addEventListener( 'click', e => this.checkOutsideModalClick( e ) );
    }

    /**
    * Handle keydown events.
    *
    * @param {object} evt The key push event.
    * @return {void}
    */
    keyDownHandler( evt ) {

        // Return early if no modal open.
        if ( ! this.$currentOpenModal ) {
            return;
        }

        evt = evt || window.event;

        const KEY_TAB = 9;
        const KEY_ESC = 27;

        switch ( evt.keyCode ) {
            case KEY_TAB:

                // Return early if no focused elements found.
                if ( ! this.$currentFirstFocusable ) {
                    evt.preventDefault();
                    break;
                }

                this.handleTabKeyEvent( evt );

                break;
            case KEY_ESC:
                this.closeModal( this.$currentOpenModal );
                break;
            default:
                break;
        } // end switch
    }

    /**
    * Handle tab key pushes inside modal.
    *
    * @param {object} evt The key push event.
    * @return {void}
    */
    handleTabKeyEvent( evt ) {

        // If shift + tab pushed.
        if ( evt.shiftKey ) {

            // Focus the last element if focus was on the first element.
            if ( this.$currentFirstFocusable.is( ':focus' ) ) {
                evt.preventDefault();
                this.$currentLastFocusable.focus();
            }
        } else {

            // Focus the first element if focus was on the last element.
            if ( this.$currentLastFocusable.is( ':focus' ) ) {
                evt.preventDefault();
                this.$currentFirstFocusable.focus();
            }
        }
    }

    /**
     * Opens or closes a modal.
     *
     * @param {object} e The clicked element.
     * @return {void}
     */
    toggleModal( e ) {
        const toggleBtn = e.target;
        let modalId;

        // Make sure that the actual correct element was clicked, and not a child of it.
        if ( toggleBtn.getAttribute( 'data-modal' ) ) {
            modalId = toggleBtn.getAttribute( 'data-modal' );
        } else {
            modalId = $( toggleBtn ).closest( '.js-modal__btn' ).data( 'modal' );
        }

        if ( ! modalId ) {
            return;
        }

        const $modal = $( '#' + modalId );

        if ( ! $modal.length ) {
            return;
        }

        if ( $modal.hasClass( 'modal--is-open' ) ) {
            this.closeModal();
        } else {
            this.openModal( $modal );
        }
    }

    /**
     * Opens a modal. Also closes other open modals.
     * @param {object} $modal The jquery modal object to open.
     * @return {void}
     */
    openModal( $modal ) {

        // Get the element that was focused before the element opened.
        this.focusedElBeforeOpen = document.activeElement;

        // Start by closing any other possible modals
        if ( this.$currentOpenModal ) {
            this.closeModal( this.$currentOpenModal );
        }

        // Add class to modal to reveal content
        $modal.addClass( 'modal--is-open' );

        // Store this so it can be closed with esc
        this.$currentOpenModal = $modal;

        // Add class to site body to prevent background-scrolling.
        this.$body.addClass( 'modal-open-on-page' );

        // Change aria state
        $modal.attr( 'aria-hidden', 'false' );

        // Set focus on first focusable element inside the modal.
        const $focusableElements = $modal.find( 'a[href], area[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), [tabindex="0"]' );

        if ( $focusableElements.length ) {
            this.$currentFirstFocusable = $focusableElements.first();
            this.$currentLastFocusable = $focusableElements.last();
            this.$currentFirstFocusable.focus();
        }
    }

    /**
     * Closes the currently opened modal.
     * @return {void}
     */
    closeModal() {

        // Return early if no open modal found.
        if ( ! this.$currentOpenModal ) {
            return;
        }

        // Remove class from modal to hide content
        this.$currentOpenModal.removeClass( 'modal--is-open' );

        // Change aria state
        this.$currentOpenModal.attr( 'aria-hidden', 'true' );

        // Remove class from body to enable scrolling.
        this.$body.removeClass( 'modal-open-on-page' );

        // Set focus on the element that opened the modal.
        this.focusedElBeforeOpen.focus();

        // Cleanup
        this.$currentOpenModal = null;
        this.focusedElBeforeOpen = null;
    }

    /**
     * Checks if clicked outside of modal.
     * @link https://stackoverflow.com/a/3028037
     *
     * @param {object} e Event object.
     * @return {void}
     */
    checkOutsideModalClick( e ) {

        // Return early if no open modal found.
        if ( ! this.$currentOpenModal ) {
            return;
        }

        // Close modal if the click event was not inside the modal__content-wrap os modal__btn items.
        if ( ! $( e.target ).closest( '.js-modal__content-wrap' ).length && ! $( e.target ).closest( '.js-modal__btn' ).length ) {
            this.closeModal( this.$currentOpenModal );
        }
    }

    /**
     * Opens cookiemodal if url hash is set depending on which site we are coming from
     */
    cookieModal() {

        let hash = window.location.hash;

        // Check if hash is "#laaturemontti". Only then show the modal popup.
        // This hash is added to url when user comes redirected from laaturemontti.fi site
        if ( hash === '#laaturemontti' ) {
            const $modal = $( document.getElementById( 'js-cookie-modal' ) );
            this.openModal( $modal );
        }

        if ( hash === '#salaojakympit' ) {
            const $modal = $( document.getElementById( 'js-cookie-modal2' ) );
            this.openModal( $modal );
        }

        if ( hash === '#nesco' ) {
            const $modal = $( document.getElementById( 'js-cookie-modal3' ) );
            this.openModal( $modal );
        }
    }

    /**
     * Run when the document is ready.
     * @return {void}
     */
    docReady() {
        this.cache();
        this.events();
        this.cookieModal();
    }
}
